import { Map as ImmutableMap } from 'immutable';
import React from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import { customersSelectors } from 'app-components/flux/customers';
import { projectsSelectors } from 'app-components/flux/projects';

import { flagFieldOptions } from 'data/transactions/config';
import { exists } from 'utils/utils';

import store from 'app-components/utils/redux-store';

import { mkProject } from 'app-components/flux/projects/projectsTypes';
import {
  CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE,
  DISPLAY_NAME_FOR_CLIENT_ONLY_UI_OPTION,
  QCS_ID_FOR_CLIENT_ONLY_UI_OPTION,
  VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION,
} from 'components/QFilterSelect/constants';

import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import OutlinedFlag from '@mui/icons-material/OutlinedFlag';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';

export const itemMenuConfig = [
  {
    resourceType: 'categories',
    title: 'Categories',
    id: 'add_filter_button_category',
    icon: <CategoryOutlinedIcon />,
  },
  {
    resourceType: 'payees',
    title: 'Payee',
    id: 'add_filter_button_payee',
    icon: <StoreOutlinedIcon />,
  },
  {
    resourceType: 'tags',
    title: 'Tag',
    id: 'add_filter_button_tag',
    icon: <LocalOfferOutlinedIcon />,
  },
  {
    resourceType: 'accounts',
    title: 'Account',
    id: 'add_filter_button_account',
    icon: <AccountBalanceOutlinedIcon />,
  },
  {
    resourceType: CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE,
    title: 'Clients & Projects',
    id: 'add_filter_button_clients_and_projects',
    icon: <GroupsOutlinedIcon />,
  },
  {
    resourceType: 'flags',
    title: 'Flag',
    id: 'add_filter_button_account',
    icon: <OutlinedFlag />,
  },
  {
    resourceType: 'advanced',
    title: 'Advanced',
    id: 'add_filter_button_advanced',
    icon: <AutoAwesomeMosaicOutlinedIcon />,
  },
];

const countDefinedItems = (imap) => {
  if (!imap) return 0;
  return imap.reduce((v, x) => v + (exists(x) && 1) || 0, 0);
};

/**
 * In Clients and Projects filter, selected child node could be project name or 'Client only' option (which has projectId value of 0).
 * This method returns customerId(parentId) of selected child node
 */
export const getCustomerIdOfSelectedProject = (selectedProjectId) => {
  // Check if user has selected "Client only" UI option.
  if (selectedProjectId.includes(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION)) {
    const items = selectedProjectId.split(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION);
    return items[0];
  } else {
    // Project name was selected by user.
    const projects = projectsSelectors.getProjects(store.getState());
    const projectObj = projects.get(selectedProjectId);
    return projectObj.customerId;
  }
};

/**
 * @param selectedIds - Flat list of selected ids (client or project ids)
 * @returns Array of objects which maintains relationship/reference between selected customer and selected projects under it
 */
export const getCustomersAndProjectsSelectedFromSelectedIds = (selectedIds) => {
  const customersById = customersSelectors.getCustomers(store.getState());
  const projectsByCustomerId = projectsSelectors.getProjectsByCustomerId(store.getState());

  // Determine whether selected Id belongs to client(customer) or project
  const customerIds = []; // Array to keep track of all selectecd client Ids
  const projectIds = []; // Array to keep track of all selectecd project Ids
  for (const id of selectedIds) {
    if (customersById.has(id)) {
      customerIds.push(id);
    } else {
      projectIds.push(id);
    }
  }
  /*
   * Construct an array of objects where each object consist of following 2 properties
   *    {
   *      customer: Customer,
   *      projects: Project[]
   *    }
   */
  const customersAndProjectsSelected = [];

  for (const customerId of customerIds) {
    // Get all projects(children) of this client
    const allProjects = projectsByCustomerId.get(customerId)?.allProjects;
    if (!allProjects) {
      // Case where selected Client(customerId) doesn't have any project.
      const item = {
        customer: customersById.get(customerId),
        projects: [new mkProject({ id: QCS_ID_FOR_CLIENT_ONLY_UI_OPTION })],
      };
      customersAndProjectsSelected.push(item);
    } else {
      const isAnyProjectSelected = allProjects.some((project) => projectIds.includes(project.id));
      if (!isAnyProjectSelected) {
        /* Case where selected Client has projects but none of it's projects are selected(as they are not shown on UI for selection).
         * This happens during 'Search' flow where user types client name in search textbox and checks
         * client name from matched search result
         */
        const item = {
          customer: customersById.get(customerId),
          projects: [new mkProject({ id: QCS_ID_FOR_CLIENT_ONLY_UI_OPTION }), ...allProjects],
        };
        customersAndProjectsSelected.push(item);
      }
    }
  }

  // For each selected projectId (which is either project name or "Client only" option), get it's customerId and update Array
  for (const projectId of projectIds) {
    // Get id of it's parent ( i.e. customerId)
    const customerId = getCustomerIdOfSelectedProject(projectId);

    // Add this customer info  in array if it doesn't exist
    const customerExist = customersAndProjectsSelected.find((item) => item?.customer?.id === customerId);
    if (!customerExist) {
      const item = { customer: customersById.get(customerId), projects: [] };
      customersAndProjectsSelected.push(item);
    }

    // Update project list for this customer
    const objIndex = customersAndProjectsSelected.findIndex((item) => item?.customer?.id === customerId);
    if (projectId.includes(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION)) {
      // This is a case where user has selected "Client only" option in Filter UI
      customersAndProjectsSelected[objIndex].projects.push(new mkProject({ id: QCS_ID_FOR_CLIENT_ONLY_UI_OPTION }));
    } else {
      // User has selected project
      const project = projectsSelectors.getProjectById(store.getState(), projectId);
      customersAndProjectsSelected[objIndex].projects.push(project);
    }
  }
  return customersAndProjectsSelected;
};

/**
 * Returns count to be displayed on Right panel of filter UI for Clients and Projects filter
 * @param filterObject - Filter Object.
 * @returns count - number of selected filter items
 */
const getClientsAndProjectsFilterItemsCount = (filterObject) => {
  const selectedClientsAndProjectsInfo = filterObject.get(CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE);
  if (!selectedClientsAndProjectsInfo) return 0;

  const customersById = customersSelectors.getCustomers(store.getState());
  let count = 0;

  for (const selectedId of selectedClientsAndProjectsInfo) {
    if (customersById?.has(selectedId)) {
      // Parent node (client name) is selected. Include this in calculation
      count += 1;
    } else {
      // User has selected child node (i.e. either project name or "Client only" UI option).
      // Get id of it's parent ( i.e. customerId)
      const customerId = getCustomerIdOfSelectedProject(selectedId);
      // Include this Project (or "Client only" UI option) in calculation only if it's Parent Node( i.e. Client name) was not selected on Filter UI
      if (!selectedClientsAndProjectsInfo.includes(customerId)) {
        count += 1;
      }
    }
  }
  return count;
};

export const countFilterItems = (filterObject) => {
  if (!filterObject) return 0;
  const count =
    ImmutableMap(filterObject.toObject())
      .filter((v, k) => k !== 'payees')
      .reduce((v, x) => v + (x?.size || 0), 0) || 0;
  return count + countDefinedItems(ImmutableMap(filterObject?.advanced.toObject()));
};

export const countFilterItemsForDisplay = (filterObject) => {
  if (!filterObject) return 0;
  let count =
    ImmutableMap(filterObject.toObject())
      .filter(
        (v, k) =>
          k !== 'payees' &&
          k !== 'categories' &&
          k !== CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE &&
          k !== 'customersAndProjectsSelected',
      )
      .reduce((v, x) => v + (x?.size || 0), 0) || 0;

  // Calculation for Clients and Projects filter
  count += getClientsAndProjectsFilterItemsCount(filterObject);

  count +=
    filterObject.get('categories')?.reduce((accum, catId) => {
      const category = categoriesSelectors.getCategoryForId(store.getState(), catId);
      const hasParent = category?.parentId !== '0';
      const parentIsSelected = hasParent && filterObject.get('categories').has(category?.parentId);
      return accum + (parentIsSelected ? 0 : 1);
    }, 0) || 0;
  return count + countDefinedItems(ImmutableMap(filterObject?.advanced.toObject()));
};

export const advancedKeyToName = {
  isBillOrSubscription: 'Bills & Subscriptions',
  isExcludedFromF2S: 'Excluded in spending plan',
  isExcludedFromReports: 'Excluded in reports',
  isReviewed: 'Reviewed',
};

/**
 * Returns name to be displayed on right panel of filter dialog when user selects client or project name from center panel
 * If client name is selected, then we return client name
 * If project name is selected, then we return <client name: project name>
 * @param searchItemId - id of item that user has selected for filtering.
 * @returns name to be displayed on Right panel of filter UI
 */
const getNameForClientsAndProjects = (searchItemId) => {
  let customerName = '';
  const customersById = customersSelectors.getCustomers(store.getState());

  // Check if customer name is selected
  if (customersById?.has(searchItemId)) {
    const customer = customersById.get(searchItemId);
    customerName = customer?.businessName || `${customer?.firstName || ''} ${customer?.lastName || ''}` || 'Unknown';
    return customerName;
  }

  // Check if this is a case where user has selected "Client only" UI option. We need to return name in format '<customer name> : Client Only'
  if (searchItemId.includes(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION)) {
    const items = searchItemId.split(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION);
    // Get customerId of selected 'Client Only' option
    const clientOnlyUIOptionCustomerId = items[0];
    if (customersById.has(clientOnlyUIOptionCustomerId)) {
      const customer = customersById.get(clientOnlyUIOptionCustomerId);
      customerName = customer?.businessName || `${customer?.firstName || ''} ${customer?.lastName || ''}` || 'Unknown';
      return `${customerName}: ${DISPLAY_NAME_FOR_CLIENT_ONLY_UI_OPTION}`;
    } else {
      return 'error';
    }
  }
  // Project name is selected. We need to return name in format '<customer name> : <project name>'
  const projectsByCustomerId = projectsSelectors.getProjectsByCustomerId(store.getState());

  for (const customer of customersById) {
    // Get all projects for this customer based on customer id
    const projects = projectsByCustomerId.get(customer[0])?.allProjects;
    if (projects) {
      for (const project of projects) {
        if (project?.id === searchItemId) {
          customerName =
            customer[1]?.businessName || `${customer[1]?.firstName || ''} ${customer[1]?.lastName || ''}` || 'Unknown';
          return `${customerName}: ${project.name}`;
        }
      }
    }
  }
  return 'error';
};

const getCategoryName = (categoriesById, it, accountsById) => {
  switch (it) {
    case 'BALANCE_ADJUSTMENT_0':
      return 'Balance Adjustment';
    case 'BALANCE_ADJUSTMENT_1':
      return 'Opening Balance';
    case 'BALANCE_ADJUSTMENT_2':
      return 'Transfer';
    case 'BALANCE_ADJUSTMENT_3':
      return 'Credit Card Payment';
    case '0':
      return 'Uncategorized';
    case 'UNCATEGORIZED':
      return 'Uncategorized';
    case 'OTHER':
      return 'Other';
    case 'TRANSFER':
      return 'Transfer';
    case 'USAGE-PERSONAL':
      return 'Personal';
    case 'USAGE-BUSINESS':
      return 'Business';
    default:
      if (categoriesById?.has(it)) {
        return categoriesById.get(it)?.name || '';
      }
      if (accountsById?.has(it)) {
        return `Transfer: ${accountsById.get(it)?.name || ''}`;
      }
      return 'error';
  }
};

export const getNameFromKeyFactory =
  (categoriesById, payeesByName, tagsById, accountsById, chartOfAccounts) => (itemKey, item) => {
    switch (itemKey) {
      case 'categories':
        return (
          chartOfAccounts?.find((coa) => item === coa.id)?.name || getCategoryName(categoriesById, item, accountsById)
        );
      case CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE:
        return getNameForClientsAndProjects(item);
      case 'payees':
        return payeesByName.get(item?.toLowerCase()) || '';
      case 'tags':
        return tagsById.get(item)?.name || '';
      case 'accounts':
        return accountsById.get(item).name || '';
      case 'flags':
        return flagFieldOptions.find(({ value }) => value === item).label;
      // return
      case 'advanced':
        return `${!item.val ? 'Not' : ''} ${advancedKeyToName[item.key]}`;
      default:
        return 'error';
    }
  };

/** Removes an item from the filter object
 * @param itemKey is one of ['categories', 'payees', 'tags', 'accounts', 'advanced']
 * @param item is the id of the category, tag or payee to remove, or the advanced property
 * @param filterObject is the current filterObject, a new one will be returned
 * @param categoriesById should be pulled from getCategoriesById selector
 * @param payeesNameList should be pulled from getPayeesNameList selector
 * @returns a new filter object with the removed item, if a parent category is removed it removes the children
 * */
export const removeItemFromFilterObject = (itemKey, item, filterObject, categoriesById, payeesNameList) => {
  let updatedObject = null;
  switch (itemKey) {
    case 'categories':
    case 'accounts':
    case 'tags':
      updatedObject = filterObject.set(itemKey, filterObject.get(itemKey).delete(item));
      if (itemKey === 'categories') {
        const childIds =
          categoriesSelectors.getChildCategoriesByParentIdUtil(categoriesById, item).keySeq().toArray() || [];

        if (childIds.length > 0)
          updatedObject = updatedObject.set('categories', updatedObject.get('categories').subtract(childIds));
      }
      break;
    case 'payees': {
      const nameToRemove = payeesNameList.find((payee) => payee.id === item)?.name;
      if (nameToRemove) {
        updatedObject = filterObject.set('payees', filterObject.get('payees').delete(item));
        updatedObject = updatedObject.set(
          'payeeNames',
          updatedObject.get('payeeNames').delete(updatedObject.get('payeeNames').indexOf(nameToRemove)),
        );
      }
      break;
    }
    case 'advanced':
      updatedObject = filterObject.set('advanced', filterObject.get('advanced').delete(item.key));
      break;
    default:
      updatedObject = filterObject;
  }
  return updatedObject.set('numFilterItems', countFilterItems(updatedObject));
};
